import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ReactiveFormsModule, FormGroup } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { FormFieldCurrencyComponent } from '../../components/shared/form-field-currency/form-field-currency.component';
import { EnquiryDialogComponent } from '../../components/shared/enquiry-dialog/enquiry-dialog.component';
import { ResultDialogComponent, ResultOutcome } from '../../components/shared/result-dialog/result-dialog.component';
import {
  EligibilityService,
  ApplicationType,
  EligibilityForm,
  ResidencyStatus,
  EmploymentStatus,
  EmploymentBasis,
  UnemploymentStatusEnum,
  ServiceabilityOption,
  CreditHistory
} from '../../services/eligibility.service';
import { UserAction } from '../../api/v4/models/CommonUtilities/user-action';

@Component({
  selector: 'app-eligibility-view',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonToggleModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    FormFieldCurrencyComponent
  ],
  templateUrl: './eligibility-view.component.html',
  styleUrl: './eligibility-view.component.scss'
})
export class EligibilityViewComponent implements OnInit, OnDestroy {
  readonly ApplicationType = ApplicationType;
  readonly UserAction = UserAction;
  readonly ResidencyStatus = ResidencyStatus;
  readonly EmploymentStatus = EmploymentStatus;
  readonly EmploymentBasis = EmploymentBasis;
  readonly UnemploymentStatusEnum = UnemploymentStatusEnum;
  readonly ServiceabilityOption = ServiceabilityOption;
  readonly CreditHistory = CreditHistory;

  get eligibilityForm(): FormGroup<EligibilityForm> {
    return this.eligibilityService.eligibilityForm;
  }

  get loanAmount(): number { return this.eligibilityService.loanAmount; }
  get weeklyRepayment(): number { return this.eligibilityService.weeklyRepayment; }
  get totalAmountRepayable(): number { return this.eligibilityService.totalAmountRepayable; }
  get resultBand(): string { return this.eligibilityService.resultBand; }
  get isPersonal(): boolean { return this.eligibilityService.isPersonal; }
  get annualInterestRatePct(): number { return this.eligibilityService.annualInterestRatePct; }
  get loanTermMonths(): number { return this.eligibilityService.loanTermMonths; }
  get establishmentFee(): number { return this.eligibilityService.establishmentFee; }
  get monthlyAdminFee(): number { return this.eligibilityService.monthlyAdminFee; }

  // Progressive step visibility
  get showAgeStep(): boolean {
    return this.isPersonal;
  }

  get showResidencyStep(): boolean {
    return this.showAgeStep
      && this.eligibilityForm.controls.isOver18.value === UserAction.Yes;
  }

  get residencyAllowsContinue(): boolean {
    const r = this.eligibilityForm.controls.residencyStatus.value;
    return r === ResidencyStatus.NZCitizenOrPermanentResident
      || r === ResidencyStatus.WorkVisa;
  }

  get showLoanStep(): boolean {
    return this.showResidencyStep && this.residencyAllowsContinue;
  }

  get vehiclePriceEntered(): boolean {
    const v = this.eligibilityForm.controls.vehiclePrice.value;
    return v != null && v > 0;
  }

  get showLoanExit(): boolean {
    return this.showLoanStep && this.vehiclePriceEntered && this.loanAmount < 2000;
  }

  get showEmploymentStep(): boolean {
    return this.showLoanStep && this.vehiclePriceEntered && this.loanAmount >= 2000;
  }

  get employmentComplete(): boolean {
    const status = this.eligibilityForm.controls.employmentStatus.value;
    if (!status) return false;
    if (status === EmploymentStatus.Employed || status === EmploymentStatus.SelfEmployed) {
      return this.eligibilityForm.controls.employmentBasis.value != null;
    }
    if (status === EmploymentStatus.Unemployed) {
      return this.eligibilityForm.controls.unemploymentStatus.value != null;
    }
    return true;
  }

  get showServiceabilityStep(): boolean {
    return this.showEmploymentStep && this.employmentComplete;
  }

  get showCreditHistoryStep(): boolean {
    return this.showServiceabilityStep
      && this.eligibilityForm.controls.serviceabilityOption.value != null;
  }

  get creditHistoryNeedsFollowUp(): boolean {
    const v = this.eligibilityForm.controls.creditHistory.value;
    return v === CreditHistory.Mixed
      || v === CreditHistory.Poor
      || v === CreditHistory.NotSure;
  }

  get showCreditFollowUpStep(): boolean {
    return this.showCreditHistoryStep && this.creditHistoryNeedsFollowUp;
  }

  get showCreditIssuesExit(): boolean {
    return this.showCreditFollowUpStep
      && this.eligibilityForm.controls.hasCreditIssues.value === UserAction.Yes;
  }

  get showResult(): boolean {
    if (!this.showCreditHistoryStep || !this.eligibilityForm.controls.creditHistory.value) {
      return false;
    }
    if (this.creditHistoryNeedsFollowUp) {
      return this.eligibilityForm.controls.hasCreditIssues.value === UserAction.No;
    }
    return true;
  }

  // The final reveal outcome shown in the popup (null while still answering).
  get resultOutcome(): ResultOutcome | null {
    if (this.showCreditIssuesExit) {
      return 'credit-issues';
    }
    if (this.showResult) {
      return this.resultBand as ResultOutcome;
    }
    return null;
  }

  private subscriptions = new Subscription();
  private resultDialogRef?: MatDialogRef<ResultDialogComponent>;

  // Questions in the order they appear. Changing any control in a step clears
  // every control in the steps that follow it, so stale downstream answers don't
  // remain revealed when an earlier answer is changed.
  private readonly stepGroups: string[][] = [
    ['applicationType'],
    ['isOver18'],
    ['residencyStatus'],
    ['vehiclePrice', 'accessories', 'deposit', 'tradeIn'],
    ['employmentStatus', 'employmentBasis', 'unemploymentStatus'],
    ['serviceabilityOption'],
    ['creditHistory', 'hasCreditIssues']
  ];

  // Dependent sub-answers cleared when their parent question changes.
  private readonly childControls: Record<string, string[]> = {
    employmentStatus: ['employmentBasis', 'unemploymentStatus'],
    creditHistory: ['hasCreditIssues']
  };

  // When an earlier answer is changed, steps after this index are collapsed for a
  // moment, then re-reveal blank (re-playing the reveal animation). null = no collapse.
  private collapseAfterStep: number | null = null;
  private static readonly COLLAPSE_MS = 250;

  constructor(
    public eligibilityService: EligibilityService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.setupDownstreamReset();
  }

  // Triggered by the Submit button shown once a final outcome is available.
  submitResult(): void {
    if (this.resultOutcome && !this.resultDialogRef) {
      this.openResultDialog(this.resultOutcome);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private setupDownstreamReset(): void {
    this.stepGroups.forEach((group, groupIndex) => {
      const laterControls = this.stepGroups.slice(groupIndex + 1).flat();
      group.forEach((name) => {
        const control = this.eligibilityForm.get(name);
        if (!control) {
          return;
        }
        this.subscriptions.add(
          control.valueChanges.subscribe(() => {
            const dependents = [...(this.childControls[name] ?? []), ...laterControls];
            const wasEdit = this.hasAnyValue(dependents);
            this.resetControls(dependents);
            // Only collapse/re-reveal when this was an edit to an already-answered
            // question — not the first time the user answers and moves forward.
            if (wasEdit) {
              this.collapseDownstream(groupIndex);
            }
          })
        );
      });
    });
  }

  private hasAnyValue(names: string[]): boolean {
    return names.some((name) => {
      const control = this.eligibilityForm.get(name);
      return !!control && control.value !== null;
    });
  }

  private resetControls(names: string[]): void {
    names.forEach((name) => {
      const control = this.eligibilityForm.get(name);
      if (control && control.value !== null) {
        control.reset(null, { emitEvent: false });
      }
    });
  }

  // Hide steps after the given index briefly, then release so the next step
  // re-mounts and replays its reveal animation.
  private collapseDownstream(stepIndex: number): void {
    this.collapseAfterStep = stepIndex;
    setTimeout(() => {
      this.collapseAfterStep = null;
    }, EligibilityViewComponent.COLLAPSE_MS);
  }

  // Used in the template to suppress a step while downstream is collapsing.
  isStepRevealed(stepIndex: number): boolean {
    return this.collapseAfterStep === null || stepIndex <= this.collapseAfterStep;
  }

  openResultDialog(outcome: ResultOutcome): void {
    this.resultDialogRef = this.dialog.open(ResultDialogComponent, {
      panelClass: 'enquiry-dialog-panel',
      width: '640px',
      maxWidth: '95vw',
      data: { outcome }
    });
    this.resultDialogRef.afterClosed().subscribe(() => {
      this.resultDialogRef = undefined;
    });
  }

  applyNow(): void {
    this.eligibilityService.saveToLocalStorage();
    this.router.navigate(['/'], { queryParamsHandling: 'preserve' });
  }

  openEnquiryForm(): void {
    this.dialog.open(EnquiryDialogComponent, {
      panelClass: 'enquiry-dialog-panel',
      width: '640px',
      maxWidth: '95vw'
    });
  }
}
